import type {
  ComplianceQuestionDto,
  ComplianceQuestionnaireDetailDto,
  ComplianceQuestionnaireListItemDto,
  ComplianceQuestionOptionDto,
  ComplianceQuestionTreeDto,
  CurrentComplianceQuestionnaireResponseDto,
  QuestionnaireSectionDto,
} from '../types/api';
import { extractNumericOrder, normalizeStringId, pickArray, unwrapToDataObject } from './questionnaireShared';

export { normalizeStringId };

/**
 * Compliance questionnaire payloads may use snake_case or alternate keys.
 * Normalizing keeps skip logic, section grouping, and ordering consistent in admin + assessments.
 */

export function normalizeQuestionnaireSections(sections: QuestionnaireSectionDto[]): QuestionnaireSectionDto[] {
  if (!sections?.length) return [];
  return sections.map(s => {
    const raw = s as unknown as Record<string, unknown>;
    const order = extractNumericOrder([s.order, raw.order, raw.section_order, raw.sectionOrder], s.order ?? 0);
    return { ...s, order };
  });
}

function extractSkipIfOptionId(q: ComplianceQuestionTreeDto, raw: Record<string, unknown>): string | null {
  const candidates: unknown[] = [
    q.skipIfOptionId,
    (q as unknown as Record<string, unknown>).SkipIfOptionId,
    raw.skip_if_option_id,
    raw.SkipIfOptionId,
    raw.skipIfOptionID,
    raw.hide_if_option_id,
    raw.HideIfOptionId,
    raw.hideIfOptionId,
    raw.logical_skip_option_id,
    raw.logicalSkipOptionId,
    raw.skip_if_option_uuid,
    raw.skipped_when_option_id,
    raw.skippedWhenOptionId,
    raw.skipWhenOptionId,
    raw.skip_when_option_id,
    (raw.logicalSkip as Record<string, unknown> | undefined)?.optionId,
    (raw.logical_skip as Record<string, unknown> | undefined)?.option_id,
    (raw.logicalSkip as Record<string, unknown> | undefined)?.id,
    (raw.logical_skip as Record<string, unknown> | undefined)?.option,
    raw.skipIfOption,
    raw.skip_if_option,
  ];
  for (const c of candidates) {
    const id = normalizeStringId(c);
    if (id) return id;
  }
  return null;
}

function extractShowIfOptionId(q: ComplianceQuestionTreeDto, raw: Record<string, unknown>): string | null {
  const candidates: unknown[] = [
    q.showIfOptionId,
    (q as unknown as Record<string, unknown>).ShowIfOptionId,
    raw.show_if_option_id,
    raw.showIfOptionId,
    raw.ShowIfOptionId,
    raw.visible_if_option_id,
    raw.visibleIfOptionId,
    raw.display_if_option_id,
    raw.displayIfOptionId,
    (raw.showIf as Record<string, unknown> | undefined)?.optionId,
    (raw.show_if as Record<string, unknown> | undefined)?.option_id,
    raw.showIf,
    raw.show_if,
  ];
  for (const c of candidates) {
    const id = normalizeStringId(c);
    if (id) return id;
  }
  return null;
}

/**
 * Skip (hide-when-selected) takes precedence when both exist — matches Question Management “hide when selected”.
 * Show-if is used when the API only exposes “visible when this option is selected”.
 */
export function isComplianceQuestionTreeVisible(
  question: ComplianceQuestionTreeDto,
  selectedOptionIds: Set<string>
): boolean {
  const skipId = question.skipIfOptionId;
  const hasSkip = skipId !== undefined && skipId !== null && skipId !== '';
  if (hasSkip) {
    return !selectedOptionIds.has(String(skipId));
  }

  const showId = normalizeStringId(question.showIfOptionId);
  if (showId) {
    return selectedOptionIds.has(showId);
  }

  return true;
}

function normalizeComplianceOption(opt: ComplianceQuestionOptionDto): ComplianceQuestionOptionDto {
  const raw = opt as unknown as Record<string, unknown>;
  const id = normalizeStringId(opt.id ?? raw.id ?? raw.option_id ?? raw.optionId);
  return {
    ...opt,
    id: id ?? opt.id,
  };
}

export function normalizeComplianceQuestionNode(q: ComplianceQuestionTreeDto): ComplianceQuestionTreeDto {
  const raw = q as unknown as Record<string, unknown>;
  const coalesceId = (camel: unknown, snake: unknown): string | null => {
    const v = camel ?? snake;
    return normalizeStringId(v);
  };

  const skipIfOptionId = extractSkipIfOptionId(q, raw);
  const sectionId = coalesceId(q.sectionId, raw.section_id);
  const parentQuestionId =
    q.parentQuestionId !== undefined && q.parentQuestionId !== null
      ? String(q.parentQuestionId)
      : raw.parent_question_id !== undefined && raw.parent_question_id !== null
        ? String(raw.parent_question_id)
        : null;
  const triggerOptionId =
    q.triggerOptionId !== undefined && q.triggerOptionId !== null
      ? String(q.triggerOptionId)
      : raw.trigger_option_id !== undefined && raw.trigger_option_id !== null
        ? String(raw.trigger_option_id)
        : null;

  const order = extractNumericOrder(
    [q.order, raw.order, raw.display_order, raw.displayOrder, raw.question_order, raw.questionOrder, raw.sort_order, raw.sortOrder],
    q.order ?? 0
  );

  let subQuestions: ComplianceQuestionTreeDto[] = [];
  if (Array.isArray(q.subQuestions) && q.subQuestions.length > 0) {
    subQuestions = q.subQuestions.map(normalizeComplianceQuestionNode);
  } else if (Array.isArray(raw.sub_questions)) {
    subQuestions = (raw.sub_questions as ComplianceQuestionTreeDto[]).map(normalizeComplianceQuestionNode);
  } else if (Array.isArray(raw.subQuestions)) {
    subQuestions = (raw.subQuestions as ComplianceQuestionTreeDto[]).map(normalizeComplianceQuestionNode);
  } else if (Array.isArray(raw.SubQuestions)) {
    subQuestions = (raw.SubQuestions as ComplianceQuestionTreeDto[]).map(normalizeComplianceQuestionNode);
  }

  let sourceOptions: ComplianceQuestionOptionDto[] = Array.isArray(q.options)
    ? q.options
    : Array.isArray(raw.options)
      ? (raw.options as ComplianceQuestionOptionDto[])
      : Array.isArray(raw.compliance_question_options)
        ? (raw.compliance_question_options as ComplianceQuestionOptionDto[])
        : Array.isArray(raw.complianceQuestionOptions)
          ? (raw.complianceQuestionOptions as ComplianceQuestionOptionDto[])
          : Array.isArray(raw.ComplianceQuestionOptions)
            ? (raw.ComplianceQuestionOptions as ComplianceQuestionOptionDto[])
            : [];

  // Fallback: some APIs may return options under an unknown key; try to detect
  // any array property that looks like a list of compliance options (has `option`).
  if (sourceOptions.length === 0) {
    for (const value of Object.values(raw)) {
      if (!Array.isArray(value) || value.length === 0) continue;
      const first = value[0] as Record<string, unknown>;
      if (first && typeof first === 'object' && 'option' in first) {
        sourceOptions = value as ComplianceQuestionOptionDto[];
        break;
      }
    }
  }

  const showIfOptionId = extractShowIfOptionId(q, raw);
  const options = sourceOptions.map(normalizeComplianceOption);

  return {
    ...q,
    skipIfOptionId,
    showIfOptionId,
    sectionId: sectionId ?? q.sectionId ?? null,
    parentQuestionId,
    triggerOptionId,
    subQuestions,
    options,
    order,
  };
}

export function normalizeComplianceQuestionTree(nodes: ComplianceQuestionTreeDto[]): ComplianceQuestionTreeDto[] {
  if (!nodes?.length) return [];
  return nodes.map(normalizeComplianceQuestionNode);
}

/** All question IDs reachable from roots (including nested subQuestions). */
export function collectComplianceQuestionTreeIds(nodes: ComplianceQuestionTreeDto[]): Set<string> {
  const ids = new Set<string>();
  const walk = (q: ComplianceQuestionTreeDto) => {
    ids.add(q.id);
    (q.subQuestions ?? []).forEach(walk);
  };
  (nodes ?? []).forEach(walk);
  return ids;
}

/** IDs from sub-questions loaded per parent option (API), including nested subQuestions on those nodes. */
export function collectComplianceQuestionIdsFromLoadedMap(
  loadedMap: Record<string, ComplianceQuestionTreeDto[]>
): Set<string> {
  const ids = new Set<string>();
  const walk = (nodes: ComplianceQuestionTreeDto[]) => {
    for (const q of nodes) {
      ids.add(q.id);
      walk(q.subQuestions ?? []);
    }
  };
  for (const list of Object.values(loadedMap)) {
    if (Array.isArray(list)) walk(list);
  }
  return ids;
}

/** IDs reachable from the merged UI tree + loaded subs (includes flat-merge-only roots). Not for POST. */
export function allowedComplianceResponseQuestionIds(
  roots: ComplianceQuestionTreeDto[],
  loadedMap: Record<string, ComplianceQuestionTreeDto[]>
): Set<string> {
  const ids = collectComplianceQuestionTreeIds(roots);
  collectComplianceQuestionIdsFromLoadedMap(loadedMap).forEach(id => ids.add(id));
  return ids;
}

/**
 * POST /assessments: same allowed set as `allowedComplianceResponseQuestionIds` when `questionnaireTreeIds`
 * includes **all** root IDs from `mergeFlatComplianceQuestionsIntoTree` (not only the pre-merge tree).
 * Prefer `allowedComplianceResponseQuestionIds(roots, loadedMap)` at submit time so flat-merge roots are never dropped.
 */
export function allowedComplianceSubmissionQuestionIds(
   questionnaireTreeIds: Iterable<string>,
   loadedMap: Record<string, ComplianceQuestionTreeDto[]>
): Set<string> {
  const ids = new Set<string>(questionnaireTreeIds);
  collectComplianceQuestionIdsFromLoadedMap(loadedMap).forEach(id => ids.add(id));
  return ids;
}

/**
 * POST /assessments validates against the same catalog as GET .../compliance/questions/facility-type/:id.
 * The questionnaire detail tree sometimes omits nested or legacy-linked rows; merge flat-only rows as extra roots
 * so every backend-required ID can be answered (same facility type).
 */
export function mergeFlatComplianceQuestionsIntoTree(
  roots: ComplianceQuestionTreeDto[],
  flatQuestions: ComplianceQuestionDto[],
  questionnaireId: string
): ComplianceQuestionTreeDto[] {
  const normalized = normalizeComplianceQuestionTree(roots);
  const qid = questionnaireId || normalized[0]?.questionnaireId || '';
  const treeIds = collectComplianceQuestionTreeIds(normalized);
  const extras: ComplianceQuestionTreeDto[] = [];

  for (const q of flatQuestions ?? []) {
    if (!q?.id || treeIds.has(q.id)) continue;
    const rawFlat = q as unknown as Record<string, unknown>;
    // Sub-questions must never be merged in as roots — they'd render as always-visible
    // top-level questions detached from their trigger option. They are reached through
    // their parent's tree/loaded sub-questions instead.
    const isSubQuestion = Boolean(
      normalizeStringId(q.parentQuestionId ?? rawFlat.parent_question_id) ||
        normalizeStringId(q.triggerOptionId ?? rawFlat.trigger_option_id)
    );
    if (isSubQuestion) continue;
    treeIds.add(q.id);
    const node = {
      id: q.id,
      question: q.question,
      questionKinyarwanda: q.questionKinyarwanda,
      questionFrench: q.questionFrench,
      questionType: q.questionType,
      weight: q.weight,
      questionnaireId: qid,
      sectionId: (q.sectionId ?? normalizeStringId(rawFlat.section_id)) ?? null,
      order: q.order ?? extractNumericOrder([rawFlat.order, rawFlat.display_order], 999999),
      skipIfOptionId: normalizeStringId(q.skipIfOptionId ?? rawFlat.skip_if_option_id) ?? null,
      showIfOptionId: normalizeStringId(q.showIfOptionId ?? rawFlat.show_if_option_id) ?? null,
      parentQuestionId: null,
      triggerOptionId: null,
      createdAt: '',
      updatedAt: '',
      options: (q.options ?? []).map(o => ({
        id: o.id,
        option: o.option,
        optionKinyarwanda: o.optionKinyarwanda,
        optionFrench: o.optionFrench,
        marks: o.marks,
      })),
      subQuestions: [],
    } as ComplianceQuestionTreeDto;
    extras.push(normalizeComplianceQuestionNode(node));
  }

  if (!extras.length) return normalized;
  return [...normalized, ...extras];
}

export function normalizeComplianceQuestionnaireDetail(
  dto: ComplianceQuestionnaireDetailDto
): ComplianceQuestionnaireDetailDto {
  return {
    ...dto,
    sections: normalizeQuestionnaireSections(dto.sections ?? []),
    questions: normalizeComplianceQuestionTree(dto.questions ?? []),
  };
}

/** Sort sections by `order`, then id (deterministic). */
export function compareSectionOrder(a: QuestionnaireSectionDto, b: QuestionnaireSectionDto): number {
  const oa = extractNumericOrder([a.order], 0);
  const ob = extractNumericOrder([b.order], 0);
  if (oa !== ob) return oa - ob;
  return a.id.localeCompare(b.id);
}

/** Sort root questions by `order`, then id (deterministic). */
export function compareComplianceQuestionOrder(a: ComplianceQuestionTreeDto, b: ComplianceQuestionTreeDto): number {
  const oa = extractNumericOrder([a.order], 0);
  const ob = extractNumericOrder([b.order], 0);
  if (oa !== ob) return oa - ob;
  return a.id.localeCompare(b.id);
}

/**
 * Admin list: section order first, then question order within section (unsectioned last).
 */
export function sortComplianceQuestionnaireRoots(
  roots: ComplianceQuestionTreeDto[],
  sections: QuestionnaireSectionDto[]
): ComplianceQuestionTreeDto[] {
  const orderedSections = [...sections].sort(compareSectionOrder);
  const sectionRank = new Map<string, number>();
  orderedSections.forEach((s, i) => sectionRank.set(s.id, i));
  const unsectionedRank = Number.MAX_SAFE_INTEGER;

  const rankOf = (q: ComplianceQuestionTreeDto) =>
    q.sectionId && sectionRank.has(q.sectionId) ? sectionRank.get(q.sectionId)! : unsectionedRank;

  return [...roots].sort((a, b) => {
    const ra = rankOf(a);
    const rb = rankOf(b);
    if (ra !== rb) return ra - rb;
    return compareComplianceQuestionOrder(a, b);
  });
}

/**
 * Normalizes different backend shapes for GET .../compliance/questionnaire/facility-type/:id/current
 * (envelope nesting, PascalCase keys, questions only on nested questionnaire, etc.).
 */
export function parseCurrentComplianceQuestionnaireResponse(
  payload: unknown
): CurrentComplianceQuestionnaireResponseDto {
  const empty: CurrentComplianceQuestionnaireResponseDto = {
    questionnaire: null,
    sections: [],
    questions: [],
  };
  if (payload === null || payload === undefined) return empty;

  const root = unwrapToDataObject(payload);

  const questionnaire =
    (root.questionnaire as ComplianceQuestionnaireListItemDto | null | undefined) ??
    (root.Questionnaire as ComplianceQuestionnaireListItemDto | null | undefined) ??
    null;

  let questions = pickArray(root, ['questions', 'Questions', 'complianceQuestions', 'ComplianceQuestions']) as
    | ComplianceQuestionTreeDto[]
    | unknown[];
  let sections = pickArray(root, ['sections', 'Sections', 'questionnaireSections', 'QuestionnaireSections']) as
    | QuestionnaireSectionDto[]
    | unknown[];

  if (questions.length === 0 && questionnaire && typeof questionnaire === 'object') {
    const qn = questionnaire as unknown as Record<string, unknown>;
    const nestedQ = pickArray(qn, ['questions', 'Questions', 'complianceQuestions', 'ComplianceQuestions']);
    if (nestedQ.length) questions = nestedQ as ComplianceQuestionTreeDto[];
    if (!sections.length) {
      const nestedS = pickArray(qn, ['sections', 'Sections', 'questionnaireSections', 'QuestionnaireSections']);
      if (nestedS.length) sections = nestedS as QuestionnaireSectionDto[];
    }
  }

  return {
    questionnaire,
    sections: sections as QuestionnaireSectionDto[],
    questions: questions as ComplianceQuestionTreeDto[],
  };
}
